import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { GIButton, Modal } from 'app-components';

import Form from '../../../lib/wrappers/Form';
import { useAppDispatch, useAppSelector, useStateCode } from '../../../hooks';
import { dateMethods } from '../../../utils/dates/dateMethods';
import { ModalContextType } from '../../../contexts/modalContext';
import UniversalInput from '../../../lib/wrappers/UniversalInput';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { environmentSelector } from '../../../selectors/commonSelectors';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import { PLAN_TYPE } from './modalTypes';
import useCustomGrouping from '../hooks/useCustomGrouping';
import CapitalizedButton from '../components/common/CapitalizedButton';

interface IModalProps extends ModalContextType {
    medicaidEndDate: string;
    coverageYear: number;
    grpForNewPlan: {
        nextCoverageYear: string;
        nextAvailableDate: string;
        earliestStartDate: string;
        remainingAptc: number;
        remainingStateSubsidy: number;
        dentalRemainingAptc: number;
        caseNumber: string;
    };
    isChangePlan: boolean;
    typeOfPlan: PLAN_TYPE;
}

export default function EndOfPHEModal({
    closeModal,
    openModal,
    medicaidEndDate,
    coverageYear,
    grpForNewPlan,
    isChangePlan,
    typeOfPlan,
}: IModalProps) {
    const { nextCoverageYear, nextAvailableDate, earliestStartDate } = grpForNewPlan;

    const { exchangeName } = useAppSelector(environmentSelector);
    const dispatch = useAppDispatch();
    const { enrollAfterCustomGrouping } = useCustomGrouping({ dispatch });

    const earliestStartDateYear = dateMethods.getYear(earliestStartDate) || 0;

    const { isCoverageStartDateAlertVisible, isCancelEndOfPHEVisible } = useStateCode();

    function onClose(e) {
        e?.preventDefault();
        closeModal('endOfPHE');
    }

    function onContinue() {
        closeModal('endOfPHE');
        enrollAfterCustomGrouping(
            {
                isChangePlan,
                groupToEnroll: grpForNewPlan,
                typeOfPlan,
                remainingAptc: grpForNewPlan.remainingAptc,
                remainingStateSubsidy: grpForNewPlan.remainingStateSubsidy,
                dentalRemainingAptc: grpForNewPlan.dentalRemainingAptc,
                caseNumber: grpForNewPlan.caseNumber,
                enablePopupForEndOfPHE: true,
                nextAvailableDateSelected: endOfPHEState.coverageDateSelected,
            },
            { openModal },
        );
    }

    const [endOfPHEState, setEndOfPHEState] = useState({
        coverageDateSelected: nextAvailableDate || earliestStartDate,
    });

    return (
        <Modal id="endOfPHEModal">
            <Modal.Header
                id="endOfPHEModalHeader"
                titleId="customGrouping.endOfPHE.header"
                onHide={onClose}
            />
            <Modal.Body id="endOfPHEModalBody">
                <ConditionalRendering isTrue={!!grpForNewPlan.nextAvailableDate}>
                    <h2>
                        <FormattedMessage
                            id="customGrouping.endOfPHE.msg1"
                            values={{ medicaidEndDate, br: <br />, exchangeName }}
                        />
                    </h2>

                    <Form>
                        <UniversalInput
                            label={{
                                isLabelVisible: false,
                            }}
                            inputData={{
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                liClass: ['text-left'],
                                currentData: endOfPHEState,
                                name: 'coverageDateSelected',
                                value: endOfPHEState.coverageDateSelected,
                                options: [
                                    {
                                        value: nextAvailableDate,
                                        text: 'customGrouping.endOfPHE.input.option.nextAvailDt',
                                    },
                                ],
                                optionsReplacements: {
                                    nextAvailableDate,
                                },
                            }}
                            errorClasses={['margin-l-0']}
                            inputActions={{
                                onFieldChange: ({ coverageDateSelected }) => setEndOfPHEState({ coverageDateSelected }),
                            }}
                        />
                    </Form>
                </ConditionalRendering>

                <ConditionalRendering isTrue={!!nextCoverageYear}>
                    <h2>
                        <FormattedMessage
                            id="customGrouping.endOfPHE.msg3"
                            values={{ medicaidEndDate, br: <br />, exchangeName }}
                        />
                    </h2>
                    <p>
                        <FormattedMessage id="customGrouping.endOfPHE.msg4" values={{ nextCoverageYear }} />{' '}
                        <a href="#">
                            <FormattedMessage id="customGrouping.endOfPHE.clickHere" />
                        </a>
                    </p>
                </ConditionalRendering>

                <ConditionalRendering isTrue={!!earliestStartDateYear}>
                    <ConditionalRendering isTrue={earliestStartDateYear >= coverageYear}>
                        <p>
                            <FormattedMessage id="customGrouping.endOfPHE.msg5" />
                        </p>
                        <Form>
                            <UniversalInput
                                label={{
                                    isLabelVisible: false,
                                }}
                                inputData={{
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    liClass: ['text-left'],
                                    currentData: endOfPHEState,
                                    name: 'coverageDateSelected',
                                    value: endOfPHEState.coverageDateSelected,
                                    options: [
                                        {
                                            value: earliestStartDate,
                                            text: 'customGrouping.endOfPHE.input.option.earliestStDt',
                                        },
                                    ],
                                    optionsReplacements: {
                                        earliestStartDate,
                                    },
                                }}
                                errorClasses={['margin-l-0']}
                                inputActions={{
                                    onFieldChange: ({ coverageDateSelected }) =>
                                        setEndOfPHEState({ coverageDateSelected }),
                                }}
                            />
                        </Form>
                        <ConditionalRendering isTrue={isCoverageStartDateAlertVisible}>
                            <p className="usa-alert usa-alert--info gi-alert gi-alert__info">
                                <FormattedMessage id="customGrouping.endOfPHE.alertMsg" />
                            </p>
                        </ConditionalRendering>
                    </ConditionalRendering>

                    <ConditionalRendering isTrue={earliestStartDateYear < coverageYear}>
                        <a>
                            <FormattedMessage id="customGrouping.endOfPHE.backToDashboard" />
                        </a>
                    </ConditionalRendering>
                </ConditionalRendering>
            </Modal.Body>
            <Modal.Controls>
                <ConditionalRendering isTrue={isCancelEndOfPHEVisible}>
                    <CapitalizedButton 
                        id="endOfPHEModalCancelButton" 
                        onClickHandler={onClose} 
                        variant="secondary" 
                        labelId="button.cancel" 
                    />
                </ConditionalRendering>
                <CapitalizedButton 
                    id="endOfPHEModalContinueButton" 
                    variant="primary" 
                    labelId="button.continue" 
                    onClickHandler={onContinue} 
                    leftMargin={true} 
                />
            </Modal.Controls>
        </Modal>
    );
}
